import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaFileInvoiceDollar, FaFilter, FaFilePdf, FaTrash, FaFileDownload } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { calcularGratificacionMensualArt50 } from '../../utils/calculosLaboralesChile';
import '../../styles/pages/RRHH/LiquidacionesPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Página de Liquidaciones de Sueldo
 * Gestión mensual de remuneraciones
 */
const LiquidacionesPage = ({ token, id_company }) => {
    const [liquidaciones, setLiquidaciones] = useState([]);
    const [colaboradores, setColaboradores] = useState([]); // Todos (para filtros)
    const [colaboradoresDisponibles, setColaboradoresDisponibles] = useState([]); // Sin finiquito pagado
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [liquidacionActual, setLiquidacionActual] = useState(null);
    const [sueldoBaseMensual, setSueldoBaseMensual] = useState(0); // Sueldo base completo (30 días)

    // Calcular mes anterior
    const getPreviousMonth = () => {
        const date = new Date();
        const prevMonth = date.getMonth(); // Mes actual -1 (0-indexed)
        const year = prevMonth === 0 ? date.getFullYear() - 1 : date.getFullYear();
        const month = prevMonth === 0 ? 12 : prevMonth;
        return { month, year };
    };

    const previousMonthData = getPreviousMonth();

    // Filtros - por defecto muestra el mes anterior
    const [filtroMes, setFiltroMes] = useState(previousMonthData.month);
    const [filtroAnio, setFiltroAnio] = useState(previousMonthData.year);
    const [filtroColaborador, setFiltroColaborador] = useState('');

    // Formulario
    const [formData, setFormData] = useState({
        id_colaborador: '',
        mes: previousMonthData.month,
        anio: previousMonthData.year,
        dias_trabajados: 30,
        sueldo_base: '',
        horas_extras: 0,
        gratificacion: 0,
        gratificacion_tipo: '', // Para saber si es artículo 50
        bono_produccion: 0,
        aguinaldo: 0,
        otros_imponibles: 0,
        colacion: 0,
        movilizacion: 0,
        otros_haberes: 0,
        afp: 0,
        afc: 0, // Nuevo campo AFC
        salud: 0,
        impuesto_unico: 0,
        anticipo: 0,
        prestamo: 0,
        otros_descuentos: 0,
        // Datos del colaborador
        afp_nombre: '',
        isapre_fonasa: '',
        gratificacion_colaborador: '',
        origen: 'MANUAL'
    });

    const [indicadoresPrevisionales, setIndicadoresPrevisionales] = useState(null);
    const isFormReady = useRef(true); // false durante la carga inicial del formulario de edición

    useEffect(() => {
        fetchColaboradores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchLiquidaciones();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroMes, filtroAnio, filtroColaborador]);

    const fetchColaboradores = async () => {
        try {
            // Cargar todos los colaboradores activos (para filtros)
            const responseAll = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, estado: 'ACTIVO' },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (responseAll.data.success) {
                setColaboradores(responseAll.data.data);
            }

            // Cargar colaboradores disponibles (sin finiquito pagado)
            const responseDisponibles = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, estado: 'ACTIVO', excluir_finiquitados: 'true' },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (responseDisponibles.data.success) {
                setColaboradoresDisponibles(responseDisponibles.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
        }
    };

    const fetchLiquidaciones = async () => {
        try {
            setLoading(true);
            const params = { id_company, mes: filtroMes, anio: filtroAnio };
            if (filtroColaborador) params.id_colaborador = filtroColaborador;

            const response = await axios.get(`${API_BASE_URL}/api/rrhh/liquidaciones`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLiquidaciones(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar liquidaciones:', error);
            Swal.fire('Error', 'No se pudieron cargar las liquidaciones', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchIndicadoresPrevisionales = async (mes, anio) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/rrhh/indicadores-previred`, {
                params: { mes, anio },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success && response.data.data) {
                setIndicadoresPrevisionales(response.data.data);
                return response.data.data;
            } else {
                Swal.fire('Advertencia', 'No se encontraron indicadores previsionales para este período', 'warning');
                return null;
            }
        } catch (error) {
            console.error('Error al cargar indicadores:', error);
            return null;
        }
    };

    // Función para calcular impuesto único según tabla SII
    const calcularImpuestoUnico = (baseImponible, utm) => {
        if (!utm || utm <= 0) return 0;

        const baseEnUTM = baseImponible / utm;

        // Tabla de impuesto único 2024-2025 (Segunda Categoría)
        const tramos = [
            { desde: 0, hasta: 13.5, factor: 0, rebaja: 0 },
            { desde: 13.5, hasta: 30, factor: 0.04, rebaja: 0.54 },
            { desde: 30, hasta: 50, factor: 0.08, rebaja: 1.74 },
            { desde: 50, hasta: 70, factor: 0.135, rebaja: 4.49 },
            { desde: 70, hasta: 90, factor: 0.23, rebaja: 11.14 },
            { desde: 90, hasta: 120, factor: 0.304, rebaja: 17.8 },
            { desde: 120, hasta: 310, factor: 0.35, rebaja: 23.32 },
            { desde: 310, hasta: Infinity, factor: 0.40, rebaja: 38.82 }
        ];

        let impuestoEnUTM = 0;

        for (const tramo of tramos) {
            if (baseEnUTM > tramo.desde && baseEnUTM <= tramo.hasta) {
                impuestoEnUTM = (baseEnUTM * tramo.factor) - tramo.rebaja;
                break;
            }
        }

        return Math.max(0, Math.round(impuestoEnUTM * utm));
    };

    // Recalcula solo el impuesto único para liquidaciones PREVIRED
    // (AFP/AFC/Salud son fijos de Previred, no se tocan)
    const recalcularSoloImpuesto = async (currentFormData) => {
        const indicadores = indicadoresPrevisionales || await fetchIndicadoresPrevisionales(currentFormData.mes, currentFormData.anio);
        if (!indicadores) return currentFormData;

        const totalImponible = parseFloat(currentFormData.sueldo_base || 0) + parseFloat(currentFormData.otros_imponibles || 0);
        const descuentosPrevisionales = parseFloat(currentFormData.afp || 0) + parseFloat(currentFormData.afc || 0) + parseFloat(currentFormData.salud || 0);
        const rentaAfecta = Math.max(0, totalImponible - descuentosPrevisionales);
        const utm = indicadores.utm || 0;

        return { ...currentFormData, impuesto_unico: calcularImpuestoUnico(rentaAfecta, utm) };
    };

    // Función para recalcular todos los descuentos cuando cambian los haberes
    const recalcularDescuentos = async (newFormData) => {
        const selectedCol = colaboradores.find(c => c.id === parseInt(newFormData.id_colaborador));
        if (!selectedCol) return newFormData;

        const indicadores = indicadoresPrevisionales || await fetchIndicadoresPrevisionales(newFormData.mes, newFormData.anio);
        if (!indicadores) return newFormData;

        // 1. Calcular total imponible
        const sueldoBase = parseFloat(newFormData.sueldo_base || 0);
        const horasExtras = parseFloat(newFormData.horas_extras || 0);
        const bonoProduccion = parseFloat(newFormData.bono_produccion || 0);

        // Gratificación Art. 50: MIN(25% del sueldo base, 4.75 IMM / 12)
        let gratificacion = 0;
        if (selectedCol.gratificacion && selectedCol.gratificacion.toLowerCase().includes('articulo 50')) {
            const ingresoMinimo = parseFloat(indicadores.renta_minima_dependientes || 0);
            gratificacion = calcularGratificacionMensualArt50(sueldoBase, ingresoMinimo);
        }

        const totalImponible = sueldoBase + horasExtras + gratificacion + bonoProduccion;

        // 2. Calcular AFP desde indicadores
        let afpDescuento = 0;
        if (selectedCol.afp && totalImponible > 0) {
            const afpNombre = selectedCol.afp.toLowerCase().replace(/\s+/g, '_');
            const afpKey = `afp_${afpNombre}_dependiente_trabajador`;
            if (indicadores[afpKey]) {
                afpDescuento = totalImponible * (parseFloat(indicadores[afpKey]) / 100);
            }
        }

        // 3. Calcular AFC según tipo de contrato
        let afcDescuento = 0;
        if (selectedCol.tipo_contrato && totalImponible > 0) {
            const tipoContrato = selectedCol.tipo_contrato.toLowerCase();
            if (tipoContrato.includes('indefinido')) {
                afcDescuento = totalImponible * 0.006; // 0.6% trabajador
            }
            // Si es plazo fijo, queda en 0
        }

        // 4. Calcular Salud: Fonasa 7% del total imponible, Isapre según cotización
        let saludDescuento = 0;
        if (selectedCol.isapre_fonasa && totalImponible > 0) {
            const sistemaPrevisional = selectedCol.isapre_fonasa.toLowerCase();
            if (sistemaPrevisional.includes('fonasa')) {
                saludDescuento = totalImponible * 0.07; // 7% del total imponible
            } else if (selectedCol.cotizacion_isapre) {
                saludDescuento = parseFloat(selectedCol.cotizacion_isapre);
            }
        }

        const descuentosPrevisionales = afpDescuento + afcDescuento + saludDescuento;

        // 5. Calcular Impuesto Único (incluye otros_imponibles en la base pero no en AFP/AFC/Salud)
        const otrosImponibles = parseFloat(newFormData.otros_imponibles || 0);
        const rentaAfecta = totalImponible + otrosImponibles - descuentosPrevisionales;
        const utm = indicadores.utm || 0;
        const impuestoUnico = calcularImpuestoUnico(rentaAfecta, utm);

        return {
            ...newFormData,
            gratificacion: Math.round(gratificacion),
            afp: Math.round(afpDescuento),
            afc: Math.round(afcDescuento),
            salud: Math.round(saludDescuento),
            impuesto_unico: impuestoUnico
        };
    };

    const handleColaboradorChange = async (e) => {
        const idColaborador = e.target.value;
        const selectedCol = colaboradores.find(c => c.id === parseInt(idColaborador));

        if (!selectedCol) {
            setFormData({
                ...formData,
                id_colaborador: ''
            });
            return;
        }

        // Validar que el colaborador tenga AFP y Salud configurados
        const camposFaltantes = [];
        if (!selectedCol.afp) camposFaltantes.push('AFP');
        if (!selectedCol.isapre_fonasa) camposFaltantes.push('Salud (Isapre/Fonasa)');

        if (camposFaltantes.length > 0) {
            Swal.fire({
                title: 'Datos Incompletos',
                html: `El colaborador <strong>${selectedCol.nombres} ${selectedCol.apellido_paterno}</strong> no tiene configurados los siguientes datos previsionales:<br><br>
                       <ul style="text-align: left; display: inline-block;">
                       ${camposFaltantes.map(c => `<li>${c}</li>`).join('')}
                       </ul><br>
                       Por favor, complete estos datos en la ficha del colaborador antes de generar una liquidación.`,
                icon: 'warning',
                confirmButtonText: 'Entendido'
            });
            setFormData({
                ...formData,
                id_colaborador: ''
            });
            return;
        }

        // Cargar indicadores previsionales para el período (popula el estado indicadoresPrevisionales)
        await fetchIndicadoresPrevisionales(formData.mes, formData.anio);

        const sueldoCompleto = parseFloat(selectedCol.sueldo_base) || 0;
        setSueldoBaseMensual(sueldoCompleto);

        // Si días trabajados < 30, ajustar sueldo base proporcionalmente
        const dias = formData.dias_trabajados || 30;
        const sueldoAjustado = dias < 30 ? Math.round((sueldoCompleto / 30) * dias) : sueldoCompleto;

        const newFormData = {
            ...formData,
            id_colaborador: idColaborador,
            sueldo_base: sueldoAjustado || '',
            gratificacion_colaborador: selectedCol.gratificacion || '',
            afp_nombre: selectedCol.afp || '',
            isapre_fonasa: selectedCol.isapre_fonasa || ''
        };

        // Recalcular todos los descuentos con la nueva data
        const updatedFormData = await recalcularDescuentos(newFormData);
        setFormData(updatedFormData);
    };

    // Efecto para recalcular descuentos cuando cambian los haberes
    // - Creación / Edición MANUAL: recalcula AFP/AFC/Salud + impuesto único
    // - Edición PREVIRED: solo recalcula impuesto único (AFP/AFC/Salud son fijos de Previred)
    // - En ambos casos se bloquea la carga inicial del formulario con isFormReady
    useEffect(() => {
        if (!isFormReady.current) return;
        if (!formData.id_colaborador) return;

        if (formData.origen === 'PREVIRED') {
            const timeoutId = setTimeout(async () => {
                const updatedFormData = await recalcularSoloImpuesto(formData);
                setFormData(updatedFormData);
            }, 500);
            return () => clearTimeout(timeoutId);
        }

        if (formData.sueldo_base) {
            const timeoutId = setTimeout(async () => {
                const updatedFormData = await recalcularDescuentos(formData);
                setFormData(updatedFormData);
            }, 500);
            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.sueldo_base, formData.horas_extras, formData.bono_produccion, formData.otros_imponibles, formData.id_colaborador, formData.dias_trabajados]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validación adicional: verificar datos previsionales del colaborador
        if (!modoEdicion) {
            const selectedCol = colaboradores.find(c => c.id === parseInt(formData.id_colaborador));
            if (selectedCol) {
                const camposFaltantes = [];
                if (!selectedCol.afp) camposFaltantes.push('AFP');
                if (!selectedCol.isapre_fonasa) camposFaltantes.push('Salud (Isapre/Fonasa)');

                if (camposFaltantes.length > 0) {
                    Swal.fire({
                        title: 'No se puede crear la liquidación',
                        html: `El colaborador no tiene configurados: <strong>${camposFaltantes.join(', ')}</strong>.<br><br>
                               Complete estos datos en la ficha del colaborador antes de generar una liquidación.`,
                        icon: 'error'
                    });
                    return;
                }
            }
        }

        try {
            const url = modoEdicion
                ? `${API_BASE_URL}/api/rrhh/liquidaciones/${liquidacionActual.id}`
                : `${API_BASE_URL}/api/rrhh/liquidaciones`;

            const method = modoEdicion ? 'put' : 'post';

            // Preparar datos excluyendo campos auxiliares
            const dataToSend = {
                id_colaborador: formData.id_colaborador,
                mes: formData.mes,
                anio: formData.anio,
                dias_trabajados: formData.dias_trabajados || 30,
                sueldo_base: formData.sueldo_base,
                horas_extras: formData.horas_extras || 0,
                gratificacion: formData.gratificacion || 0,
                bono_produccion: formData.bono_produccion || 0,
                aguinaldo: formData.aguinaldo || 0,
                otros_imponibles: formData.otros_imponibles || 0,
                colacion: formData.colacion || 0,
                movilizacion: formData.movilizacion || 0,
                otros_haberes: formData.otros_haberes || 0,
                afp: formData.afp || 0,
                afc: formData.afc || 0,
                salud: formData.salud || 0,
                impuesto_unico: formData.impuesto_unico || 0,
                anticipo: formData.anticipo || 0,
                prestamo: formData.prestamo || 0,
                otros_descuentos: formData.otros_descuentos || 0,
                id_company
            };

            const response = await axios[method](url, dataToSend, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const pdfUrl = response.data.pdf_url;

                if (pdfUrl) {
                    // Mostrar mensaje con opción de descargar el PDF
                    Swal.fire({
                        title: 'Éxito',
                        html: `Liquidación ${modoEdicion ? 'actualizada' : 'generada'} correctamente.<br><br>El PDF ha sido generado.`,
                        icon: 'success',
                        showCancelButton: true,
                        confirmButtonText: 'Descargar PDF',
                        cancelButtonText: 'Cerrar'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            // Abrir el PDF en una nueva ventana
                            window.open(pdfUrl.startsWith('http') ? pdfUrl : `${API_BASE_URL}/uploads/${pdfUrl}`, '_blank');
                        }
                    });
                } else {
                    Swal.fire('Éxito', `Liquidación ${modoEdicion ? 'actualizada' : 'creada'} correctamente`, 'success');
                }

                setShowModal(false);
                resetForm();
                fetchLiquidaciones();
            }
        } catch (error) {
            console.error('Error al guardar liquidación:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
        }
    };

    const handleGenerarPdf = async (liquidacion) => {
        try {
            Swal.fire({
                title: 'Generando PDF...',
                text: 'Por favor espere',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading()
            });

            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/liquidaciones/${liquidacion.id}/generar-pdf`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                Swal.fire({
                    title: 'PDF Generado',
                    text: 'El PDF ha sido generado exitosamente',
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonText: 'Descargar PDF',
                    cancelButtonText: 'Cerrar'
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.open(response.data.data.pdf_url.startsWith('http') ? response.data.data.pdf_url : `${API_BASE_URL}/uploads/${response.data.data.pdf_url}`, '_blank');
                    }
                });
                fetchLiquidaciones();
            }
        } catch (error) {
            console.error('Error al generar PDF:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al generar el PDF', 'error');
        }
    };

    const handleDelete = async (liquidacion) => {
        const result = await Swal.fire({
            title: '¿Eliminar liquidación?',
            html: `¿Está seguro de eliminar la liquidación de <strong>${liquidacion.nombres} ${liquidacion.apellido_paterno}</strong> del período ${meses[liquidacion.mes - 1]} ${liquidacion.anio}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`${API_BASE_URL}/api/rrhh/liquidaciones/${liquidacion.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success) {
                    Swal.fire('Eliminada', 'La liquidación ha sido eliminada correctamente', 'success');
                    fetchLiquidaciones();
                }
            } catch (error) {
                console.error('Error al eliminar liquidación:', error);
                Swal.fire('Error', error.response?.data?.message || 'Error al eliminar la liquidación', 'error');
            }
        }
    };

    const handleEdit = (liquidacion) => {
        isFormReady.current = false; // bloquear recálculo durante la carga
        setModoEdicion(true);
        setLiquidacionActual(liquidacion);

        // Recalcular el sueldo base mensual completo a partir del sueldo guardado y los días
        const dias = liquidacion.dias_trabajados || 30;
        const sueldoGuardado = parseFloat(liquidacion.sueldo_base) || 0;
        const sueldoMensualCompleto = dias < 30 ? Math.round((sueldoGuardado / dias) * 30) : sueldoGuardado;
        setSueldoBaseMensual(sueldoMensualCompleto);

        setFormData({
            id_colaborador: liquidacion.id_colaborador,
            mes: liquidacion.mes,
            anio: liquidacion.anio,
            dias_trabajados: liquidacion.dias_trabajados || 30,
            sueldo_base: liquidacion.sueldo_base || '',
            horas_extras: liquidacion.horas_extras || 0,
            gratificacion: liquidacion.gratificacion || 0,
            gratificacion_tipo: '',
            bono_produccion: liquidacion.bono_produccion || 0,
            aguinaldo: liquidacion.aguinaldo || 0,
            otros_imponibles: liquidacion.otros_imponibles || 0,
            colacion: liquidacion.colacion || 0,
            movilizacion: liquidacion.movilizacion || 0,
            otros_haberes: liquidacion.otros_haberes || 0,
            afp: liquidacion.afp || 0,
            afc: liquidacion.afc || 0,
            salud: liquidacion.salud || 0,
            impuesto_unico: liquidacion.impuesto_unico || 0,
            anticipo: liquidacion.anticipo || 0,
            prestamo: liquidacion.prestamo || 0,
            otros_descuentos: liquidacion.otros_descuentos || 0,
            afp_nombre: '',
            isapre_fonasa: '',
            gratificacion_colaborador: '',
            origen: liquidacion.origen || 'MANUAL'
        });
        // Habilitar recálculo después de que React procese el nuevo estado
        setTimeout(() => { isFormReady.current = true; }, 600);
        setShowModal(true);
    };

    const resetForm = () => {
        const prevMonth = getPreviousMonth();
        setFormData({
            id_colaborador: '',
            mes: prevMonth.month,
            anio: prevMonth.year,
            dias_trabajados: 30,
            sueldo_base: '',
            horas_extras: 0,
            gratificacion: 0,
            gratificacion_tipo: '',
            bono_produccion: 0,
            aguinaldo: 0,
            otros_imponibles: 0,
            colacion: 0,
            movilizacion: 0,
            otros_haberes: 0,
            afp: 0,
            afc: 0,
            salud: 0,
            impuesto_unico: 0,
            anticipo: 0,
            prestamo: 0,
            otros_descuentos: 0,
            afp_nombre: '',
            isapre_fonasa: '',
            gratificacion_colaborador: ''
        });
        setModoEdicion(false);
        setLiquidacionActual(null);
        setIndicadoresPrevisionales(null);
        setSueldoBaseMensual(0);
    };

    const meses = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const esPrevired = modoEdicion && formData.origen === 'PREVIRED';

    return (
        <div className="liquidaciones-container">
            <div className="liquidaciones-header">
                <h1><FaFileInvoiceDollar /> Liquidaciones de Sueldo</h1>
                <button className="btn-nuevo" onClick={() => { resetForm(); setShowModal(true); }}>
                    <FaPlus /> Nueva Liquidación
                </button>
            </div>

            {/* Filtros */}
            <div className="liquidaciones-filters">
                <div className="filter-group">
                    <label><FaFilter /> Mes</label>
                    <select value={filtroMes} onChange={(e) => setFiltroMes(parseInt(e.target.value))}>
                        {meses.map((mes, index) => (
                            <option key={index} value={index + 1}>{mes}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Año</label>
                    <select value={filtroAnio} onChange={(e) => setFiltroAnio(parseInt(e.target.value))}>
                        {[2023, 2024, 2025, 2026].map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Colaborador</label>
                    <select value={filtroColaborador} onChange={(e) => setFiltroColaborador(e.target.value)}>
                        <option value="">Todos</option>
                        {colaboradores.map(col => (
                            <option key={col.id} value={col.id}>
                                {col.nombres} {col.apellido_paterno}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabla de liquidaciones */}
            {loading ? (
                <div className="loading">Cargando liquidaciones...</div>
            ) : (
                <div className="liquidaciones-table-container">
                    <table className="liquidaciones-table">
                        <thead>
                            <tr>
                                <th>Colaborador</th>
                                <th>Período</th>
                                <th>Total Haberes</th>
                                <th>Total Descuentos</th>
                                <th>Líquido a Pagar</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {liquidaciones.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '30px' }}>
                                        No hay liquidaciones para este período
                                    </td>
                                </tr>
                            ) : (
                                liquidaciones.map((liq) => (
                                    <tr key={liq.id}>
                                        <td>{liq.nombres} {liq.apellido_paterno}</td>
                                        <td>{meses[liq.mes - 1]} {liq.anio}</td>
                                        <td className="amount">${parseInt(liq.total_haberes || 0).toLocaleString('es-CL')}</td>
                                        <td className="amount">${parseInt(liq.total_descuentos || 0).toLocaleString('es-CL')}</td>
                                        <td className="amount liquido">${parseInt(liq.liquido_pagar || 0).toLocaleString('es-CL')}</td>
                                        <td className="actions-cell">
                                            {liq.pdf_url ? (
                                                <button
                                                    className="btn-pdf"
                                                    onClick={() => window.open(liq.pdf_url.startsWith('http') ? liq.pdf_url : `${API_BASE_URL}/uploads/${liq.pdf_url}`, '_blank')}
                                                    title="Descargar PDF"
                                                >
                                                    <FaFilePdf />
                                                </button>
                                            ) : (
                                                <button
                                                    className="btn-generate-pdf"
                                                    onClick={() => handleGenerarPdf(liq)}
                                                    title="Generar PDF"
                                                >
                                                    <FaFileDownload />
                                                </button>
                                            )}
                                            <button className="btn-edit" onClick={() => handleEdit(liq)} title="Editar">
                                                <FaEdit />
                                            </button>
                                            <button className="btn-delete" onClick={() => handleDelete(liq)} title="Eliminar">
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content liquidaciones-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{modoEdicion ? 'Editar Liquidación' : 'Nueva Liquidación'}</h2>
                        {modoEdicion && formData.origen === 'PREVIRED' && (
                            <div className="previred-edit-notice">
                                Liquidación cargada desde Previred. Solo se pueden editar los haberes no imponibles.
                            </div>
                        )}
                        <form onSubmit={handleSubmit}>
                            <div className="form-section">
                                <h3>Información General</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Colaborador *</label>
                                        <select
                                            value={formData.id_colaborador}
                                            onChange={handleColaboradorChange}
                                            required
                                            disabled={modoEdicion}
                                        >
                                            <option value="">Seleccione...</option>
                                            {(modoEdicion ? colaboradores : colaboradoresDisponibles).map(col => (
                                                <option key={col.id} value={col.id}>
                                                    {col.nombres} {col.apellido_paterno}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Mes *</label>
                                        <select
                                            value={formData.mes}
                                            onChange={(e) => setFormData({...formData, mes: parseInt(e.target.value)})}
                                            required
                                            disabled={esPrevired}
                                        >
                                            {meses.map((mes, index) => (
                                                <option key={index} value={index + 1}>{mes}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Año *</label>
                                        <input
                                            type="number"
                                            value={formData.anio}
                                            onChange={(e) => setFormData({...formData, anio: parseInt(e.target.value)})}
                                            required
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Días Trabajados</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="30"
                                            value={formData.dias_trabajados}
                                            disabled={esPrevired}
                                            onChange={(e) => {
                                                const dias = parseInt(e.target.value) || 0;
                                                if (sueldoBaseMensual > 0) {
                                                    const sueldoAjustado = dias < 30 ? Math.round((sueldoBaseMensual / 30) * dias) : sueldoBaseMensual;
                                                    setFormData({...formData, dias_trabajados: dias, sueldo_base: sueldoAjustado});
                                                } else {
                                                    setFormData({...formData, dias_trabajados: dias});
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Haberes</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Sueldo Base *</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.sueldo_base || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, sueldo_base: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            required
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Horas Extras</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.horas_extras || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, horas_extras: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Gratificación {formData.gratificacion_colaborador && `(${formData.gratificacion_colaborador})`}</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.gratificacion || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, gratificacion: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Bono Producción</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.bono_produccion || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, bono_produccion: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Aguinaldo</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.aguinaldo || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, aguinaldo: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Otros Imponibles</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.otros_imponibles || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, otros_imponibles: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Total Imponible</label>
                                        <input
                                            type="text"
                                            value={
                                                (parseInt(formData.sueldo_base || 0) +
                                                parseInt(formData.horas_extras || 0) +
                                                parseInt(formData.gratificacion || 0) +
                                                parseInt(formData.bono_produccion || 0) +
                                                parseInt(formData.aguinaldo || 0) +
                                                parseInt(formData.otros_imponibles || 0)).toLocaleString('es-CL')
                                            }
                                            readOnly
                                            className="input-readonly"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Colación</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.colacion || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, colacion: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Movilización</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.movilizacion || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, movilizacion: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Otros Haberes</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.otros_haberes || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, otros_haberes: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Total No Imponible</label>
                                        <input
                                            type="text"
                                            value={
                                                (parseInt(formData.colacion || 0) +
                                                parseInt(formData.movilizacion || 0) +
                                                parseInt(formData.otros_haberes || 0)).toLocaleString('es-CL')
                                            }
                                            readOnly
                                            className="input-readonly"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Descuentos</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>AFP {formData.afp_nombre && `(${formData.afp_nombre})`}</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.afp || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, afp: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>AFC (Fondo de Cesantía)</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.afc || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, afc: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Salud {formData.isapre_fonasa && `(${formData.isapre_fonasa})`}</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.salud || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, salud: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Impuesto Único</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.impuesto_unico || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, impuesto_unico: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Anticipo</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.anticipo || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, anticipo: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Préstamo</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.prestamo || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, prestamo: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Otros Descuentos</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={parseInt(formData.otros_descuentos || 0).toLocaleString('es-CL')}
                                            onChange={(e) => setFormData({...formData, otros_descuentos: parseInt(e.target.value.replace(/\D/g, '') || 0)})}
                                            disabled={esPrevired}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Sueldo Líquido */}
                            {(() => {
                                const totalHaberes = parseFloat(formData.sueldo_base || 0) + parseFloat(formData.horas_extras || 0) +
                                    parseFloat(formData.gratificacion || 0) + parseFloat(formData.bono_produccion || 0) +
                                    parseFloat(formData.aguinaldo || 0) + parseFloat(formData.otros_imponibles || 0) +
                                    parseFloat(formData.colacion || 0) +
                                    parseFloat(formData.movilizacion || 0) + parseFloat(formData.otros_haberes || 0);
                                const totalDescuentos = parseFloat(formData.afp || 0) + parseFloat(formData.afc || 0) +
                                    parseFloat(formData.salud || 0) + parseFloat(formData.impuesto_unico || 0) +
                                    parseFloat(formData.anticipo || 0) + parseFloat(formData.prestamo || 0) +
                                    parseFloat(formData.otros_descuentos || 0);
                                const liquido = totalHaberes - totalDescuentos;
                                return (
                                    <div className="form-section liquido-section">
                                        <div className="liquido-display">
                                            <label>Sueldo Líquido a Pagar</label>
                                            <span className="liquido-monto">${Math.round(liquido).toLocaleString('es-CL')}</span>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save">
                                    {modoEdicion ? 'Actualizar Liquidación' : 'Generar Liquidación'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiquidacionesPage;
